/**
 * people/api.ts — the profile sub-resource calls that are not the profile itself.
 *
 * Today that is one: the department's support champion (`UR-F10`, `PR-F21`/`PR-F22`/`PR-F26`). It is
 * a SEPARATE call rather than a field on the profile because the champion belongs to a DEPARTMENT,
 * not to the person being viewed — several colleagues share one, and `PR-F22` is explicit that the
 * appointment is "the organisation's, not the individual's". See `routes/people.ts` for the server
 * side of the same argument.
 */
import { apiGet } from '../tools/api';

/**
 * The support champion for a department, as `GET /api/people/:userId/support-champion` returns them
 * (backend `db/publicProfile.ts` `SupportChampionContact`).
 *
 * `department` is the department the APPOINTMENT covers, so the page can say whose champion this is
 * and make `PR-F22` true in words rather than implying the person chose the role.
 *
 * `email` is the `PR-F26` contact path. The backend returns it ONLY for an appointed champion and
 * never for a profile subject — being contactable is the entire content of the appointment.
 */
export interface SupportChampion {
  userId: string;
  name: string;
  department: string;
  email: string;
  avatarRef: string | null;
  avatarKind: string | null;
  photoRef: string | null;
}

/** The endpoint's body: `null` is the normal answer for a department with no champion (`UR-F11`). */
interface SupportChampionResponse {
  champion: SupportChampion | null;
}

/**
 * Fetch the support champion for the department the given person sits in, or null when there is none.
 *
 * NEVER THROWS, AND NULL IS NOT AN ERROR (`UR-F11`/`PR-F23`: "show nothing rather than a
 * placeholder"). A department with no champion, a signed-out session, a 500 and an unreachable
 * network all collapse to null, and the page then renders nothing at all in that spot. That is
 * deliberate: this is a secondary detail on somebody else's profile, so it must never be able to fail
 * the page it decorates — the profile load already decides whether the page renders.
 *
 * The id is URL-encoded even though the server only accepts digits: encoding at the call site is the
 * habit that keeps a future non-numeric id from becoming a path-traversal question.
 */
export async function fetchSupportChampion(userId: string): Promise<SupportChampion | null> {
  const res = await apiGet<SupportChampionResponse>(
    `/api/people/${encodeURIComponent(userId)}/support-champion`,
  );
  if (res.kind !== 'ok' || !res.body) return null;
  const champion = res.body.champion;
  // A response is untrusted data: only a champion with the two fields the card cannot render without
  // (a name to display and a department to attribute the appointment to) counts as one.
  if (!champion || typeof champion.name !== 'string' || champion.name.trim() === '') return null;
  if (typeof champion.department !== 'string' || champion.department.trim() === '') return null;
  return champion;
}
